import { Component, Input, Output, EventEmitter, inject, OnInit, signal, computed, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { InputComponent2 } from '../input2';
import { PillComponent } from '../pill/pill';
import { RecentReportsComponent, RecentReport } from '../recent-reports/recent-reports.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { CommentService, Comment, CreateCommentDto } from '../../../shared/services/comment.service';
import { PosthogService } from '../../../shared/services/posthog.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

// ===== Report types & status options (previously in status-report-section) =====

/**
 * Report type enum - matches backend PointReportType
 */
export enum PointReportType {
  // === OPERATIONAL STATUS ===
  FULL = 'FULL',
  OVERFLOWING = 'OVERFLOWING',
  BROKEN = 'BROKEN',
  MISSING = 'MISSING',
  CLOSED = 'CLOSED',
  PERMANENTLY_CLOSED = 'PERMANENTLY_CLOSED',

  // === CONTAMINATION ===
  CONTAMINATED = 'CONTAMINATED',
  WRONG_MATERIALS = 'WRONG_MATERIALS',

  // === ACCESSIBILITY ===
  BLOCKED = 'BLOCKED',
  HARD_TO_FIND = 'HARD_TO_FIND',
  RELOCATED = 'RELOCATED',

  // === HYGIENE ===
  DIRTY = 'DIRTY',
  BAD_SMELL = 'BAD_SMELL',
  PESTS = 'PESTS',

  // === DATA QUALITY ===
  POORLY_LABELED = 'POORLY_LABELED',
  WRONG_LOCATION = 'WRONG_LOCATION',
  WRONG_INFO = 'WRONG_INFO',
  ABANDONED = 'ABANDONED',

  // === SERVICE QUALITY (places) ===
  LONG_WAIT = 'LONG_WAIT',
  POOR_SERVICE = 'POOR_SERVICE',
  NOT_ACCEPTING = 'NOT_ACCEPTING',

  // === POSITIVE ===
  WORKING_FINE = 'WORKING_FINE',
  RECENTLY_EMPTIED = 'RECENTLY_EMPTIED',
  NEWLY_INSTALLED = 'NEWLY_INSTALLED',
  GOOD_SERVICE = 'GOOD_SERVICE',
}

/** Status option interface for display */
export interface StatusOption {
  id: PointReportType;
  label: string;
  icon: string;
}

/** Entity types that can have status reports */
export type ReportableEntityType = 'container' | 'place' | 'route' | 'event';

/** Status options by entity type */
export const STATUS_OPTIONS: Record<ReportableEntityType, StatusOption[]> = {
  container: [
    { id: PointReportType.FULL, label: 'socialSection.containerReports.full', icon: 'assets/icons/container.svg' },
    { id: PointReportType.WORKING_FINE, label: 'socialSection.containerReports.goodService', icon: 'assets/icons/check-circle.svg' },
    { id: PointReportType.BROKEN, label: 'socialSection.containerReports.broken', icon: 'assets/icons/wrench.svg' },
    { id: PointReportType.MISSING, label: 'socialSection.containerReports.notHere', icon: 'assets/icons/location-off.svg' },
    { id: PointReportType.CONTAMINATED, label: 'socialSection.containerReports.mixed', icon: 'assets/icons/trash.svg' },
    { id: PointReportType.BLOCKED, label: 'socialSection.containerReports.inaccessible', icon: 'assets/icons/construction.svg' },
    { id: PointReportType.DIRTY, label: 'socialSection.containerReports.dirty', icon: 'assets/icons/biohazard.svg' },
  ],
  place: [
    { id: PointReportType.WORKING_FINE, label: 'socialSection.placeReports.goodService', icon: 'assets/icons/check-circle.svg' },
    { id: PointReportType.PERMANENTLY_CLOSED, label: 'socialSection.placeReports.closed', icon: 'assets/icons/circle-x.svg' },
    { id: PointReportType.MISSING, label: 'socialSection.placeReports.notHere', icon: 'assets/icons/location-off.svg' },
    { id: PointReportType.LONG_WAIT, label: 'socialSection.placeReports.longWait', icon: 'assets/icons/time.svg' },
  ],
  route: [
    { id: PointReportType.MISSING, label: 'socialSection.routeReports.didNotPass', icon: 'assets/icons/location-off.svg' },
    { id: PointReportType.WORKING_FINE, label: 'socialSection.routeReports.passedCorrectly', icon: 'assets/icons/check-circle.svg' },
    { id: PointReportType.BLOCKED, label: 'socialSection.routeReports.blockedStreet', icon: 'assets/icons/construction.svg' },
    { id: PointReportType.WRONG_INFO, label: 'socialSection.routeReports.incorrectInfo', icon: 'assets/icons/exclamation.svg' },
    { id: PointReportType.RELOCATED, label: 'socialSection.routeReports.routeChanged', icon: 'assets/icons/route-nodes.svg' },
  ],
  event: [
    { id: PointReportType.CLOSED, label: 'socialSection.eventReports.cancelled', icon: 'assets/icons/circle-x.svg' },
    { id: PointReportType.MISSING, label: 'socialSection.eventReports.doesNotExist', icon: 'assets/icons/location-off.svg' },
    { id: PointReportType.WRONG_LOCATION, label: 'socialSection.eventReports.wrongLocation', icon: 'assets/icons/location-off.svg' },
    { id: PointReportType.WRONG_INFO, label: 'socialSection.eventReports.incorrectInfo', icon: 'assets/icons/exclamation.svg' },
    { id: PointReportType.WORKING_FINE, label: 'socialSection.eventReports.allCorrect', icon: 'assets/icons/check-circle.svg' },
  ],
};

/** Color variant by entity type */
export const COLOR_VARIANTS: Record<ReportableEntityType, 'primary' | 'secondary' | 'tertiary' | 'cuaternary'> = {
  container: 'secondary',
  place: 'primary',
  route: 'cuaternary',
  event: 'primary',
};

/**
 * Unified comment item for display
 */
export interface SocialItem {
  id: string;
  text: string;
  date: Date;
  author: string;
  type: 'comment' | 'report';
  reportTypes?: string[];
}

/**
 * SocialSectionComponent - Unified social section for all point modals
 *
 * Combines:
 * - Recent reports display
 * - Status report selection (multi-select, does NOT auto-send)
 * - Comment input (shared with report flow)
 * - Comments list
 *
 * The status report and comment share the same backend endpoint (POST /api/v1/comments).
 * When reportTypes[] is present, the backend creates a 'report' type comment.
 * The user can select statuses, optionally add a comment, then submit once.
 */
@Component({
  selector: 'app-social-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputComponent2,
    PillComponent,
    RecentReportsComponent,
    SpinnerComponent,
    TranslatePipe
  ],
  templateUrl: './social-section.component.html',
  styleUrl: './social-section.component.scss'
})
export class SocialSectionComponent implements OnInit, AfterViewInit {
  private commentService = inject(CommentService);
  private posthog = inject(PosthogService);
  private t = inject(TranslationService);

  /** The entity ID */
  @Input({ required: true }) entityId!: string;

  /** The entity type for status options */
  @Input({ required: true }) entityType!: ReportableEntityType;

  /** Max visible recent reports */
  @Input() maxVisibleReports: number = 3;

  /** Emitted after a successful submission */
  @Output() interactionAdded = new EventEmitter<{ type: 'comment' | 'report'; success: boolean }>();

  // --- State ---
  selectedStatuses = signal<Set<string>>(new Set());
  commentText = signal<string>('');
  isSending = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  sendSuccess = signal<boolean>(false);
  /** All items (comments + reports) loaded from backend */
  comments = signal<SocialItem[]>([]);

  /** Computed: recent reports extracted from loaded comments */
  recentReports = computed(() => {
    return this.comments()
      .filter(c => c.type === 'report' && c.reportTypes && c.reportTypes.length > 0)
      .map(c => ({
        id: c.id,
        reportTypes: c.reportTypes || [],
        upvotes: 0,
        downvotes: 0,
        createdAt: c.date instanceof Date ? c.date.toISOString() : String(c.date),
        userName: c.author,
      } as RecentReport));
  });

  // Scroll arrows for status pills
  @ViewChild('statusScrollWrapper') statusScrollWrapper!: ElementRef<HTMLElement>;
  @ViewChild('commentInput') commentInput!: InputComponent2;
  showLeftArrow = signal<boolean>(false);
  showRightArrow = signal<boolean>(true);

  /** Bound property for input component */
  newCommentText: string = '';

  /** Get status options for entity type */
  get statuses(): StatusOption[] {
    return STATUS_OPTIONS[this.entityType] ?? [];
  }

  /** Get color variant for entity type */
  get colorVariant(): 'primary' | 'secondary' | 'tertiary' | 'cuaternary' {
    return COLOR_VARIANTS[this.entityType] ?? 'secondary';
  }

  /** Whether a status is selected */
  isStatusSelected(statusId: string): boolean {
    return this.selectedStatuses().has(statusId);
  }

  /** Whether any status is selected (shows the input area) */
  get hasSelectedStatuses(): boolean {
    return this.selectedStatuses().size > 0;
  }

  /** Whether the submit button should be enabled */
  get canSubmit(): boolean {
    return (this.hasSelectedStatuses || !!this.newCommentText.trim()) && !this.isSending();
  }

  /** Button label based on what's being submitted */
  get submitLabel(): string {
    if (this.hasSelectedStatuses && this.newCommentText.trim()) {
      return this.t.instant('socialSection.submitReport');
    }
    if (this.hasSelectedStatuses) {
      return this.t.instant('socialSection.submitReport');
    }
    return this.t.instant('socialSection.commentBtn');
  }

  ngOnInit() {
    this.loadComments();
  }

  ngAfterViewInit() {
    setTimeout(() => this.updateScrollArrows(), 100);
  }

  // --- Status selection (toggle, multi-select) ---
  toggleStatus(statusId: string) {
    if (this.isSending()) return;

    this.sendSuccess.set(false);
    const current = new Set(this.selectedStatuses());
    if (current.has(statusId)) {
      current.delete(statusId);
    } else {
      current.add(statusId);
    }
    this.selectedStatuses.set(current);
  }

  // --- Scroll handling ---
  onStatusScroll() {
    this.updateScrollArrows();
  }

  private updateScrollArrows() {
    if (!this.statusScrollWrapper?.nativeElement) return;

    const el = this.statusScrollWrapper.nativeElement;
    const scrollLeft = el.scrollLeft;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;

    this.showLeftArrow.set(scrollLeft > 5);
    this.showRightArrow.set(scrollLeft < scrollWidth - clientWidth - 5);
  }

  scrollStatusLeft() {
    if (!this.statusScrollWrapper?.nativeElement) return;

    const el = this.statusScrollWrapper.nativeElement;
    const pills = el.querySelectorAll('app-pill');
    const currentScroll = el.scrollLeft;

    let targetScroll = 0;
    for (let i = pills.length - 1; i >= 0; i--) {
      const pill = pills[i] as HTMLElement;
      const pillLeft = pill.offsetLeft - 16;
      if (pillLeft < currentScroll - 5) {
        targetScroll = pillLeft;
        break;
      }
    }

    el.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  scrollStatusRight() {
    if (!this.statusScrollWrapper?.nativeElement) return;

    const el = this.statusScrollWrapper.nativeElement;
    const pills = el.querySelectorAll('app-pill');
    const currentScroll = el.scrollLeft;
    const clientWidth = el.clientWidth;

    for (let i = 0; i < pills.length; i++) {
      const pill = pills[i] as HTMLElement;
      const pillRight = pill.offsetLeft + pill.offsetWidth;
      if (pillRight > currentScroll + clientWidth + 5) {
        const targetScroll = pill.offsetLeft - 16;
        el.scrollTo({ left: targetScroll, behavior: 'smooth' });
        break;
      }
    }
  }

  // --- Submit (unified: report + comment use same endpoint) ---
  async submit() {
    if (!this.canSubmit) {
      // If disabled, focus the comment input to encourage writing
      if (this.commentInput) {
        this.commentInput.setFocus();
      }
      return;
    }

    if (!this.entityId) return;

    this.isSending.set(true);
    this.sendSuccess.set(false);

    const reportTypes = Array.from(this.selectedStatuses());
    const content = this.newCommentText.trim();

    try {
      const dto: CreateCommentDto = {
        entityType: 'point',
        entityId: this.entityId,
      };

      if (content) {
        dto.content = content;
      }

      if (reportTypes.length > 0) {
        dto.reportTypes = reportTypes;
      }

      const result = await this.commentService.create(dto).toPromise();

      // Add to local list
      const newItem: SocialItem = {
        id: result?.id || Date.now().toString(),
        text: content,
        date: new Date(),
        author: result?.author?.username || 'Usuario',
        type: reportTypes.length > 0 ? 'report' : 'comment',
        reportTypes: reportTypes.length > 0 ? reportTypes : undefined,
      };
      this.comments.update(items => [newItem, ...items]);

      // Reset
      this.selectedStatuses.set(new Set());
      this.newCommentText = '';
      this.commentText.set('');
      this.sendSuccess.set(true);

      const type = reportTypes.length > 0 ? 'report' : 'comment';
      this.interactionAdded.emit({ type, success: true });

      this.posthog.capture(`${this.entityType}_social_interaction`, {
        entityId: this.entityId,
        entityType: this.entityType,
        type,
        reportTypes,
        hasComment: !!content,
      });

      // Clear success after 3s
      setTimeout(() => {
        this.sendSuccess.set(false);
      }, 3000);

    } catch (error: any) {
      // Toast is handled by CommentService — just update UI state
      this.interactionAdded.emit({ type: reportTypes.length > 0 ? 'report' : 'comment', success: false });

      this.posthog.capture(`${this.entityType}_social_interaction_error`, {
        entityId: this.entityId,
        entityType: this.entityType,
        error: String(error),
      });
    } finally {
      this.isSending.set(false);
    }
  }

  // --- Load comments ---
  async loadComments(): Promise<void> {
    if (!this.entityId) return;

    this.isLoading.set(true);
    try {
      const response = await this.commentService.findByEntity({
        entityType: 'point',
        entityId: this.entityId,
      }).toPromise();

      if (response?.data && Array.isArray(response.data)) {
        this.comments.set(response.data.map((c: any) => ({
          id: c.id,
          text: c.content || '',
          date: new Date(c.createdAt),
          author: c.author?.username || 'Usuario',
          type: c.reportTypes?.length ? 'report' : 'comment',
          reportTypes: c.reportTypes,
        })));
      }
    } catch (error: any) {
      // Toast is handled by CommentService
      this.comments.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  // --- Helpers ---
  getStatusLabel(statusId: string): string {
    const status = this.statuses.find(s => s.id === statusId);
    if (!status?.label) return statusId;
    return this.t.instant(status.label);
  }

  getStatusIcon(statusId: string): string {
    const status = this.statuses.find(s => s.id === statusId);
    return status?.icon || 'assets/icons/exclamation.svg';
  }

  formatDate(date: Date): string {
    if (!date) return '';

    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Hace un momento' : `Hace ${minutes} min`;
      }
      return hours === 1 ? 'Hace 1 hora' : `Hace ${hours}h`;
    }
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;

    return new Date(date).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /** Track function for ngFor */
  trackById(_: number, item: SocialItem): string {
    return item.id;
  }
}
